'use client';

import {
    AuthenticPlacesHeroFrame,
    AuthenticPlacesRootFrame, AuthenticPlacesSectionContentFrame, AuthenticPlacesSectionFrame
} from "@/app/features/sites/authentic-places/ui/shared/AuthenticPlacesFrames";
import AuthenticPlacesMapView from "@/app/features/sites/authentic-places/ui/shared/AuthenticPlacesMapView";
import DefaultHeroIntroContentEditable
    from "@/app/features/shared/intros/hero/ui/editable/DefaultHeroIntroContent.editable";
import {
    useSiteAuthenticPlacesEditorContext
} from "@/app/features/sites/authentic-places/contexts/SiteAuthenticPlaceEditorContext";
import { AuthenticPlace, Enum_Componentlinkslink_Type } from "@/app/lib/generated/graphql-types";
import LoadingSpinner from "@/app/__to_be_integrated/_____components/_NEW/LoadingSpinner";
import { useMemo } from "react";
import AuthenticPlaceEditorProvider, {
    useAuthenticPlaceEditorContext
} from "@/app/features/sites/authentic-places/contexts/AuthenticPlaceEditorContext";
import SplitContent from "@/app/features/shared/split-content/ui/SplitContent";
import VisiblyScrollableV2 from "@/app/features/shared/__to_be_integrated/ui/v2/VisiblyScrollable";
import { Box, Typography } from "@mui/material";
import { Stack } from "@mui/system";
import EditableTypographyV2 from "@/app/features/shared/editable-typography/EditableTypographyV2";
import EditableMarkdownV2 from "@/app/features/shared/editable-markdown/EditableMarkdownV2";
import AuthenticPlaceSectionBackgroundEditable
    from "@/app/features/sites/authentic-places/ui/editable/AuthenticPlaceSectionBackground.editable";
import useMarkdownEditScrollLock
    from "@/app/features/shared/editable-markdown/hooks/useMarkdownEditScrollLock";

export default function AuthenticPlacesEditable({ places }: { places: AuthenticPlace[] }) {

    const { isLoading, editable, updateEditable, errorForField } = useSiteAuthenticPlacesEditorContext();

    const links = useMemo(() => places.map(place => ({
        id: `local-link-${place.documentId}`,
        name: place.title,
        type: Enum_Componentlinkslink_Type.Internal,
        url: `#${place.documentId}`,
    })), [places]);

    const effectiveHeroData  = useMemo(() => {
        if (!editable) return null;

        return ({
            ...editable.intro,
            links,
        })
    }, [editable, links]);

    function handleTitleChange(title: string) {
        updateEditable({
            intro: {
                title
            }
        })
    }

    function handleSubtitleChange(subtitle: string) {
        updateEditable({
            intro: {
                subtitle
            }
        })
    }

    function handleDescriptionChange(description: string | undefined) {
        updateEditable({
            intro: {
                description
            }
        })
    }

    if (isLoading || !editable) return <LoadingSpinner />;
    if (!editable) return null;

    return (
        <AuthenticPlacesRootFrame>
            <AuthenticPlacesHeroFrame
                content={(
                    <DefaultHeroIntroContentEditable
                        data={effectiveHeroData || editable.intro}
                        onTitleChange={handleTitleChange}
                        onSubtitleChange={handleSubtitleChange}
                        onDescriptionChange={handleDescriptionChange}
                        errorForField={errorForField}
                    />
                )}
                background={<AuthenticPlacesMapView places={places} />}
            />

            {places.map((place, index) => (
                <AuthenticPlacesSectionFrame
                    key={place.documentId}
                    sectionId={place.documentId}
                    headingId={`${place.documentId}-heading`}
                >
                    <AuthenticPlaceEditorProvider documentId={place.documentId}>
                        <AuthenticPlaceSectionEditable
                            place={place}
                            headingId={`${place.documentId}-heading`}
                            orientation={index % 2 == 0 ? 'row' : 'row-reverse'}
                        />
                    </AuthenticPlaceEditorProvider>
                </AuthenticPlacesSectionFrame>
            ))}
        </AuthenticPlacesRootFrame>
    )
}

function AuthenticPlaceSectionEditable({ place, headingId, orientation }: { place: AuthenticPlace, headingId: string, orientation?: Orientation }) {

    const { editable, updateEditable, errorForField } = useAuthenticPlaceEditorContext();

    const { isMarkdownEditing, onMarkdownEditingChange, markdownEditingContainerSx } = useMarkdownEditScrollLock();

    function handleTitleChange(title: string) {
        updateEditable({
            title
        })
    }

    function handleDescriptionChange(description: string | undefined) {
        updateEditable({
            description
        })
    }

    function handleRouteDescriptionChange(routeDescription: string | undefined) {
        updateEditable({
            routeDescription
        })
    }

    const titleError = errorForField?.('title') || undefined;
    const descriptionError = errorForField?.('description') || undefined;
    const routeDescriptionError = errorForField?.('routeDescription') || undefined;

    return (
        <>
            <SplitContent
                alignment={orientation}
                variant="plain"
                content={
                    <AuthenticPlacesSectionContentFrame>
                        <VisiblyScrollableV2 scrollDisabled={isMarkdownEditing}>
                            <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
                                <EditableTypographyV2
                                    label="Titel"
                                    variant="h2"
                                    component="h1"
                                    id={headingId}
                                    color="primary"
                                    value={editable?.title || ''}
                                    onChange={handleTitleChange}
                                    helperText={titleError}
                                    error={Boolean(titleError)}
                                />
                                <Box sx={markdownEditingContainerSx}>
                                    <EditableMarkdownV2
                                        label="Beschreibung"
                                        value={editable?.description || ''}
                                        onChange={handleDescriptionChange}
                                        onEditingChange={onMarkdownEditingChange}
                                        typographyOverrides={{ paragraph: "body2" }}
                                        error={Boolean(descriptionError)}
                                        helperText={descriptionError}
                                    />
                                </Box>
                            </Stack>
                        </VisiblyScrollableV2>
                        <Stack spacing={1}>
                            <Typography
                                variant="subtitle2"
                                textTransform="uppercase"
                                color="primary"
                            >
                                Wegbeschreibung
                            </Typography>

                            <EditableMarkdownV2
                                label="Wegeschreibung"
                                value={editable?.routeDescription || ''}
                                onChange={handleRouteDescriptionChange}
                                typographyOverrides={{ paragraph: 'body2'}}
                                error={Boolean(routeDescriptionError)}
                                helperText={routeDescriptionError}
                            />
                        </Stack>
                    </AuthenticPlacesSectionContentFrame>
                }
                background={
                    <AuthenticPlaceSectionBackgroundEditable place={place} />
                }
            />
        </>
    )
}
